import { toString } from 'lodash';
import { DASHBOARD_COLUMN_VOCABULARY } from 'common/types/dashboard/dashboardVocabularies';
import {
  COLUMN_TYPES,
  DEFAULT_FIRM_SUMMARY_COLUMNS,
  getValueType,
  isDraftSensitiveValueType,
  PROCESS_MANAGEMENT_COLUMN_TYPES,
  SpreadsheetDefinition,
} from 'common/types/summaryDefinitions';
import type { ValueType } from 'common/types/summaryDefinitions/valueTypes/valueType';
import {
  FIRM_SUMMARY_DRAFT_TITLE,
  FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE,
  FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
} from 'pages/Firms/common/constants';
import { FundByCompany } from 'pages/Firms/hooks/useFirmSummaryTab';
import { generateColumnKey, getArrayValue, getObjectValue, shortDate } from 'utilities';
import { CreateColumnsParams, FirmSummaryColumn, FirmSummaryColumnCell } from './createColumns.types';

/**
 * Column generator functions by column type
 * Each function creates a FirmSummaryColumn for that column type
 */
type ColumnGenerator = (fundsByCompany: FundByCompany[]) => FirmSummaryColumn;

const toOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatBreakevenExitEquityValue = (minValue: number | null, maxValue: number | null): string | null => {
  const hasMin = typeof minValue === 'number' && Number.isFinite(minValue);
  const hasMax = typeof maxValue === 'number' && Number.isFinite(maxValue);

  if (!hasMin && !hasMax) {
    return null;
  }

  const formatNumber = (value: number) => value.toLocaleString('en-US', { maximumFractionDigits: 0 });

  if (hasMin && hasMax && minValue !== maxValue) {
    return `${formatNumber(minValue)} - ${formatNumber(maxValue)}`;
  }

  const valueToFormat = hasMax ? maxValue : minValue;
  return valueToFormat == null ? null : formatNumber(valueToFormat);
};

const columnGenerators: Record<string, ColumnGenerator> = {
  [FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, company_slug: companySlug, fund_name: fundName } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = {
        companyName,
        companySlug,
        fundName,
        value: null,
      };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.FUND_NAME]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, fund_name: fundName } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: fundName };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.VALUATION_DATE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        measurement_date: fundValuationDate,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: shortDate(fundValuationDate) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.INITIAL_INVESTMENT_DATE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        initial_investment_date: fundInitialInvestmentDate,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: shortDate(fundInitialInvestmentDate) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.MOST_RECENT_INVESTMENT_DATE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        most_recent_investment_date: fundMostRecentInvestmentDate,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: shortDate(fundMostRecentInvestmentDate) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.DILUTED_OWNERSHIP]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        diluted_ownership: fundFullyDilutedOwnershipPercentage,
        fund_name: fundName,
        funds_count: fundsCount,
        is_parent: isParent,
        parent_type: parentType,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      const value =
        isParent && parentType === 'fund' ? null : toOptionalNumber(fundFullyDilutedOwnershipPercentage);
      accumulator[columnKey] = { fundsCount, value };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.OUTSTANDING_OWNERSHIP]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        outstanding_ownership: fundOutstandingOwnershipPercentage,
        fund_name: fundName,
        funds_count: fundsCount,
        is_parent: isParent,
        parent_type: parentType,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      const value =
        isParent && parentType === 'fund' ? null : toOptionalNumber(fundOutstandingOwnershipPercentage);
      accumulator[columnKey] = { fundsCount, value };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.INVESTED_CAPITAL]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_cost_basis: fundinvestedCapital,
        fund_name: fundName,
        funds_count: fundsCount,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(fundinvestedCapital) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.REALIZED_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        realized_value: fundRealizedValue,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(fundRealizedValue) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.UNREALIZED_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        unrealized_value: fundUnrealizedValue,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(fundUnrealizedValue) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.CURRENT_FUND_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        current_fund_value: currentFundValue,
        fund_name: fundName,
        funds_count: fundsCount,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(currentFundValue) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.TOTAL_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        total_value: fundTotalValue,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(fundTotalValue) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        breakeven_exit_equity_value_max: breakevenMax,
        breakeven_exit_equity_value_min: breakevenMin,
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      const formattedValue = formatBreakevenExitEquityValue(
        toOptionalNumber(breakevenMin),
        toOptionalNumber(breakevenMax)
      );
      accumulator[columnKey] = { fundsCount, value: formattedValue };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.EQUITY_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        equity_value: equityValue,
        fund_name: fundName,
        funds_count: fundsCount,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(equityValue) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.ENTERPRISE_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        enterprise_value: enterpriseValue,
        fund_name: fundName,
        funds_count: fundsCount,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(enterpriseValue) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.ENTERPRISE_VALUATION_METHOD]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        enterprise_valuation_method: enterpriseValuationMethod,
        fund_name: fundName,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: enterpriseValuationMethod };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.VALUATION_APPROACH]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        valuation_approach: valuationApproach,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: valuationApproach };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.ALLOCATION_METHOD]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        allocation_method: allocationMethod,
        company_name: companyName,
        fund_name: fundName,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: allocationMethod };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.DCF_DISCOUNT_RATE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        dcf_discount_rate: discountRate,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: toOptionalNumber(discountRate) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.PROJECTED_REVENUE_YEAR_1]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        projected_revenue_year_1: projectedRevenue,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: toOptionalNumber(projectedRevenue) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.PROJECTED_REVENUE_YEAR_2]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        projected_revenue_year_2: projectedRevenue,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: toOptionalNumber(projectedRevenue) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.PROJECTED_REVENUE_YEAR_3]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        projected_revenue_year_3: projectedRevenue,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: toOptionalNumber(projectedRevenue) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.VALUATION_STATUS]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        valuation_status: valuationStatus,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: valuationStatus };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.PERCENT_CHANGE_FROM_PREVIOUS]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        percent_change_from_previous: percentChange,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(percentChange) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.TOTAL_INSIGHT_PREFERENCE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        total_insight_preference: totalInsightPreference,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(totalInsightPreference) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.FUND_PREFERENCE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        fund_preference: fundPreference,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(fundPreference) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.SPECIFIC_PREFERENCE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        specific_preference: specificPreference,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(specificPreference) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.TOTAL_COMPANY_PREFERENCE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        total_company_preference: totalCompanyPreference,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { fundsCount, value: toOptionalNumber(totalCompanyPreference) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.IMPLIED_ARR_MULTIPLE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        implied_arr_multiple: impliedArrMultiple,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: toOptionalNumber(impliedArrMultiple) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.GROSS_IRR]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, fund_name: fundName, gross_irr: fundGrossIRR } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: toOptionalNumber(fundGrossIRR) };
      return accumulator;
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.MOIC]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_cost_basis: fundinvestedCapital,
        fund_name: fundName,
        moic,
        total_value: fundTotalValue,
      } = getObjectValue(current);
      const investedCapital = toOptionalNumber(fundinvestedCapital);
      const totalValue = toOptionalNumber(fundTotalValue);
      let fundMOIC = toOptionalNumber(moic);
      if (fundMOIC === null && investedCapital !== null && totalValue !== null) {
        fundMOIC = investedCapital > 0 ? totalValue / investedCapital : 0;
      }
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      accumulator[columnKey] = { value: fundMOIC };
      return accumulator;
    }, {} as FirmSummaryColumn),
};

/**
 * Every column in the Looking Glass catalog is selectable in a spreadsheet definition, not only the
 * ones with a bespoke generator above. The definition-data endpoint already returns each selected
 * column on the row under its public name, so extraction is a lookup plus the display coercion the
 * value-type registry describes - `dataSource.path` where an id and its row field disagree
 * (`valuation_date` reads `measurement_date`), the id itself otherwise.
 *
 * The bespoke generators still win for the columns they cover: they read legacy row aliases, carry
 * fund metadata, or assemble a value no single field holds.
 */
const catalogValue = (value: unknown, valueType?: ValueType | null): FirmSummaryColumnCell['value'] => {
  if (value === null || value === undefined) {
    return null;
  }

  // A list column (valuation_methods) is declared as text and arrives as an array.
  if (Array.isArray(value)) {
    return value.length ? value.map(entry => toString(entry)).join(', ') : null;
  }

  const format = valueType?.displayFormat;
  if (!format) {
    return toString(value);
  }

  switch (format.type) {
    case 'currency':
    case 'percentage':
    case 'number':
      // A missing or non-numeric value renders blank rather than as a fabricated 0.
      return toOptionalNumber(value);
    case 'date':
      return shortDate(toString(value));
    case 'boolean':
      return value ? (format.trueLabel ?? 'Yes') : (format.falseLabel ?? 'No');
    default:
      return toString(value);
  }
};

const createCatalogGenerator =
  (columnType: string): ColumnGenerator =>
    fundsByCompany => {
      const valueType = getValueType(columnType);
      const dataSource = valueType?.dataSource;
      const path = dataSource?.type === 'field' ? dataSource.path : columnType;

      return getArrayValue(fundsByCompany).reduce((accumulator, current) => {
        const {
          company_name: companyName,
          fund_name: fundName,
          funds_count: fundsCount,
          source_row: sourceRow,
        } = getObjectValue(current);
        const columnKey = generateColumnKey({
          id: companyName,
          name: fundName,
          prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
        });
        accumulator[columnKey] = {
          fundsCount,
          value: catalogValue((getObjectValue(sourceRow) as Record<string, unknown>)[path], valueType),
        };
        return accumulator;
      }, {} as FirmSummaryColumn);
    };

/**
 * Column types that can be rendered in Firm Summary: the bespoke generators plus the whole Looking
 * Glass catalog. Used to differentiate supported vs unsupported columns in the Add column dropdown.
 */
export const SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES = new Set<string>([
  ...Object.keys(columnGenerators),
  ...DASHBOARD_COLUMN_VOCABULARY,
]);

// The synthetic task-widget column plus the four data columns behind it, all gated by the same firm
// flag. Before the catalog opened up only the synthetic one was reachable.
const PROCESS_MANAGEMENT_GATED_COLUMN_TYPES = new Set<string>([
  FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE,
  ...PROCESS_MANAGEMENT_COLUMN_TYPES,
]);

/**
 * Whether a column type may be offered in the Add column dropdown and the column editor.
 */
export const isAddableFirmSummaryColumnType = (columnType: string, isProcessManagementEnabled?: boolean): boolean =>
  SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES.has(columnType) &&
  (Boolean(isProcessManagementEnabled) || !PROCESS_MANAGEMENT_GATED_COLUMN_TYPES.has(columnType));

export const getRenderedFirmSummaryColumnTypes = (
  columnTypes: string[],
  isProcessManagementEnabled?: boolean
): string[] => {
  // Process Management is now a fully configurable column: its position comes from the
  // definition like any other column. The firm flag only acts as an availability gate -
  // when it is off, strip the column so flag-off firms never render it even if a
  // definition happens to include it.
  if (isProcessManagementEnabled) {
    return columnTypes;
  }

  return columnTypes.filter(columnType => !PROCESS_MANAGEMENT_GATED_COLUMN_TYPES.has(columnType));
};

/**
 * Get ordered column types from a spreadsheet definition
 */
export function getOrderedColumnTypes(definition?: SpreadsheetDefinition): string[] {
  if (!definition?.columns) {
    // Fall back to default columns (excluding company_name as it's handled separately by rows)
    return DEFAULT_FIRM_SUMMARY_COLUMNS.filter(col => col !== COLUMN_TYPES.COMPANY_NAME);
  }

  // Sort by column index and extract column types, excluding company_name
  return Object.entries(definition.columns)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, colType]) => colType)
    .filter(col => col !== COLUMN_TYPES.COMPANY_NAME);
}

const createColumns = (params: CreateColumnsParams & { orderedColumnTypes?: string[] }): FirmSummaryColumn[] => {
  const { fundsByCompany, spreadsheetDefinition, orderedColumnTypes } = params;

  // Get ordered column types from definition or use defaults
  const columnTypes = orderedColumnTypes ?? getOrderedColumnTypes(spreadsheetDefinition);

  // Generators only extract values; one pass here stamps the draft flags on the
  // columns the value-type registry marks as draft-sensitive. Rollup and leaf
  // rows alike carry their own flags from the row normalizer.
  const draftFlagsByColumnKey = getArrayValue(fundsByCompany).map(item => {
    const {
      company_name: companyName,
      fund_name: fundName,
      is_final: isFinal,
      is_published: isPublished,
    } = getObjectValue(item);

    return {
      columnKey: generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      }),
      isDraft: !isPublished && !isFinal,
    };
  });

  const columns: FirmSummaryColumn[] = [];

  columnTypes.forEach(columnType => {
    // Never skip: createParser pairs this array with orderedColumnTypes by index, so dropping a
    // column here would retitle every column after it. An unknown type renders blank instead.
    const generator = columnGenerators[columnType] ?? createCatalogGenerator(columnType);

    const column = generator(fundsByCompany);
    const valueType = getValueType(columnType);

    if (valueType && isDraftSensitiveValueType(valueType)) {
      draftFlagsByColumnKey.forEach(({ columnKey, isDraft }) => {
        const cell = column[columnKey];
        if (cell) {
          cell.isDraft = isDraft;
          // Only Total Value renders the DRAFT literal; other columns just blank out
          if (columnType === COLUMN_TYPES.TOTAL_VALUE) {
            cell.draftValue = FIRM_SUMMARY_DRAFT_TITLE;
          }
        }
      });
    }

    columns.push(column);
  });

  return columns;
};

export default createColumns;
